import { useMemo } from "react";
import { useRecoilValue } from "recoil";
import {
  AlertOctagon,
  Bell,
  Briefcase,
  Building2,
  CalendarCheck,
  ClipboardList,
  Download,
  File,
  FileBarChart,
  LayoutDashboard,
  Tag,
  Users,
} from "lucide-react";
import { userData } from "../../store";
import {
  AccountingMenuFlag,
  DirectoriesMenuFlag,
  DocumentsMenuFlag,
  ImportMenuFlag,
  ListingsMenuFlag,
  MaintenanceMenuFlag,
  NotificationsMenuFlag,
  PortfoliosMenuFlag,
  ReportsMenuFlag,
  TasksMenuFlag,
} from "../../featureFlags/mainMenuFlags";
import {
  BankTransactionsMenuFlag,
  BatchChargesMenuFlag,
  ChartOfAccountsMenuFlag,
  CompanyFinancialsMenuFlag,
  ConnectedBankAccountsMenuFlag,
  DisputesMenuFlag,
} from "../../featureFlags/accountingFeatureFalgs";
import {
  MaintenanceTechDirectoryMenuFlag,
  OwnersDirectoryMenuFlag,
  TenantDirectoryMenuFlag,
  UsersDirectoryMenuFlag,
  VendorDirectoryMenuFlag,
} from "../../featureFlags/directoriesFeatureFlags";
import { isProPmProduct, isSelfManageProduct } from "../../auth/accessUtils";

/**
 * Shared hook that returns the main navigation menu items for both the
 * horizontal top navbar (ModernNavbar) and the sidebar (SidebarNavbar).
 * The `isPrimary` field is used by ModernNavbar for the mobile bottom bar.
 */
export const useMenuItems = () => {
  const userInfo = useRecoilValue(userData);

  return useMemo(
    () =>
      [
        {
          accessor: "home",
          id: "home",
          label: "Dashboard",
          path: "/dashboard",
          icon: <LayoutDashboard className="w-5 h-5" />,
          hide: false,
          isPrimary: true,
        },
        {
          accessor: "portfolio",
          id: "portfolio",
          label: "Portfolios",
          path: "/portfolios",
          icon: <Briefcase className="w-5 h-5" />,
          hide: !PortfoliosMenuFlag(),
          isPrimary: false,
        },
        {
          accessor: "units",
          id: "units",
          label: "Units",
          path: "/units",
          icon: <Building2 className="w-5 h-5" />,
          hide: false,
          isPrimary: false,
        },
        {
          accessor: "maintenance",
          id: "maintenance",
          label: "Maintenance",
          path: "/maintenance",
          icon: <AlertOctagon className="w-5 h-5" />,
          hide: !MaintenanceMenuFlag(),
          isPrimary: false,
        },
        {
          accessor: "accounting",
          id: "accounting",
          label: "Accounting",
          path: "/accounting",
          icon: <FileBarChart className="w-5 h-5" />,
          hide: !AccountingMenuFlag(),
          isPrimary: true,
          children: [
            {
              label: "Company Financials",
              id: "companyFinancials",
              path: "/accounting/companyFinancials",
              hide: !CompanyFinancialsMenuFlag(),
            },
            {
              label: "Chart of Accounts",
              id: "chartOfAcc",
              path: "/accounting/chartOfAccounts",
              hide: !ChartOfAccountsMenuFlag(),
            },
            {
              label: "Connected Bank Accounts",
              id: "connectedBankAcc",
              path: "/accounting/connectedBankAccounts",
              hide: !ConnectedBankAccountsMenuFlag(),
            },
            {
              label: "Bank Transactions",
              id: "bankTransactions",
              path: "/accounting/bankTransactions",
              hide: !BankTransactionsMenuFlag(),
            },
            {
              label: "Batch Charges",
              id: "batchCharges",
              path: "/accounting/batchCharges",
              hide: !BatchChargesMenuFlag(),
            },
            {
              label: "Customer Directory",
              id: "customerDirectory",
              path: "/accounting/customerDirectory",
              hide: !BankTransactionsMenuFlag(),
            },
            {
              label: "Pay Bills",
              id: "payBills",
              path: "/accounting/payBills",
              hide: !BankTransactionsMenuFlag(),
            },
            {
              label: "Disputes",
              id: "disputes",
              path: "/accounting/disputes",
              hide: !DisputesMenuFlag(),
            },
          ].filter((item) => !item.hide),
        },
        isSelfManageProduct(userInfo?.clientType) && {
          accessor: "reports",
          id: "reports",
          label: "Reports",
          path: "/reports",
          icon: <ClipboardList className="w-5 h-5" />,
          hide: !ReportsMenuFlag(),
          isPrimary: false,
        },
        isProPmProduct(userInfo?.clientType) && {
          accessor: "reports",
          id: "reports",
          label: "Reports",
          path: "/reports",
          icon: <ClipboardList className="w-5 h-5" />,
          hide: !ReportsMenuFlag(),
          isPrimary: true,
          children: [
            {
              label: "Reports Directory",
              id: "reportsDirectory",
              path: "/reports",
              hide: !ReportsMenuFlag(),
            },
            {
              label: "Statement Directory",
              id: "statementDirectory",
              path: "/reports/statements",
              hide: false,
            },
          ].filter((item) => !item.hide),
        },
        {
          accessor: "directories",
          id: "directories",
          label: "Directories",
          path: "/directories",
          icon: <Users className="w-5 h-5" />,
          hide: !DirectoriesMenuFlag(),
          isPrimary: true,
          children: [
            {
              label: "Users",
              id: "usersDirectory",
              path: "/directories/users",
              hide: !UsersDirectoryMenuFlag(),
            },
            {
              label: "Owners",
              id: "ownersDirectory",
              path: "/directories/owners",
              hide: !OwnersDirectoryMenuFlag(),
            },
            {
              label: "Tenants",
              id: "tenantsDirectory",
              path: "/directories/tenants/dashboard",
              hide: !TenantDirectoryMenuFlag(),
            },
            {
              label: "Vendors",
              id: "vendorsDirectry",
              path: "/directories/vendor",
              hide: !VendorDirectoryMenuFlag(),
            },
            {
              label: "Maintenance Techs",
              id: "techDirectory",
              path: "/directories/maintenanceTech",
              hide: !MaintenanceTechDirectoryMenuFlag(),
            },
          ].filter((item) => !item.hide),
        },
        {
          accessor: "listings",
          id: "listings",
          label: "Leads",
          path: "/listings",
          icon: <Tag className="w-5 h-5" />,
          hide: !ListingsMenuFlag(),
          isPrimary: false,
          children: [
            {
              label: "Listing",
              id: "listing",
              path: "/listings",
              hide: !ListingsMenuFlag(),
            },
            {
              label: "Intake Forms",
              id: "intakeForms",
              path: "/intakeForms",
              hide: !(ListingsMenuFlag() && isProPmProduct(userInfo?.clientType)),
            },
          ].filter((item) => !item.hide),
        },
        {
          accessor: "documents",
          id: "documents",
          label: "Documents",
          path: "/documents",
          icon: <File className="w-5 h-5" />,
          hide: !DocumentsMenuFlag(),
          isPrimary: false,
        },
        {
          accessor: "import",
          id: "import",
          label: "Import",
          path: "/importCSV",
          icon: <Download className="w-5 h-5" />,
          hide: !ImportMenuFlag(),
          isPrimary: false,
        },
        {
          accessor: "notifications",
          id: "notifications",
          label: "Notifications",
          path: "/notifications",
          icon: <Bell className="w-5 h-5" />,
          hide: !NotificationsMenuFlag(),
          isPrimary: false,
        },
        {
          accessor: "tasks",
          id: "tasks",
          label: "Tasks Center",
          path: "/tasks",
          icon: <CalendarCheck className="w-5 h-5" />,
          hide: !TasksMenuFlag(),
          isPrimary: false,
        },
      ].filter((item) => item && !item.hide),
    [userInfo?.clientType],
  );
};
